/**
 * PATH: packages/ui/src/kanban/components/KanbanCardTitleRow.tsx
 * FILE_ID: UI-KANBAN-CARD-TITLEROW-001
 * PURPOSE: The card title row — left-gutter completion check, the title
 *   (display + Trello-style inline rename), and the sensitive / pinned /
 *   AI-confidence trailing markers.
 * WHY: Extracted from KanbanCard.tsx (≤500-line working limit). Owns the
 *   self-contained rename + complete-toggle local state (nothing outside the
 *   title row reads them), so the card orchestrator stays lean. Markup is
 *   byte-identical to the former inline block.
 * FLOW:
 *   ┌──────────────┐   ┌──────────────────────┐   ┌──────────────┐
 *   │ dbl-click    │ → │ rename state machine │ → │ commit via   │
 *   │ title        │   │ (draft / Esc / blur) │   │ onRenameInline│
 *   └──────────────┘   └──────────────────────┘   └──────────────┘
 * RELATED:
 *   - ./KanbanCard.tsx (renders this as the first row of the card body).
 *   - ./KanbanCardCompleteButton (the gutter check).
 *   - ../../components/global-search/highlight (title match highlight).
 */
import { useEffect, useRef, useState } from 'react'
import { Lock, Pin, Sparkles } from 'lucide-react'

import type { Card as KanbanCardModel } from '../types'
import { highlightMatch } from '../../components/global-search/highlight'
import { KanbanCardCompleteButton } from './KanbanCardCompleteButton'

export interface KanbanCardTitleRowProps {
  card: KanbanCardModel
  isCompact: boolean
  /** FEAT-2026-06-24-KANBAN-CARD-SIZE — 'large' density: bump the title one
   *  step (12.5px → 14px) so the roomier card reads bigger, not just padded. */
  large?: boolean
  /** Reveal the gutter completion check (card hovered + not dragging/dropping). */
  completeVisible: boolean
  onOpen?: (card: KanbanCardModel) => void
  onRenameInline?: (card: KanbanCardModel, nextTitle: string) => Promise<unknown> | void
  onToggleComplete?: (card: KanbanCardModel) => Promise<unknown> | void
  highlightQuery?: string
  /** Pre-computed AI-confidence percentage (null → no pill). */
  aiConfidencePct: number | null
  lowConfidence: boolean
  hidePin?: boolean
  hideAi?: boolean
  hideSensitive?: boolean
}

export function KanbanCardTitleRow({
  card,
  isCompact,
  large = false,
  completeVisible,
  onOpen,
  onRenameInline,
  onToggleComplete,
  highlightQuery,
  aiConfidencePct,
  lowConfidence,
  hidePin = false,
  hideAi = false,
  hideSensitive = false,
}: KanbanCardTitleRowProps) {
  // Complete-toggle in-flight flag — the check shows a wait cursor and
  // can't double-fire the status mutation while pending.
  const [togglePending, setTogglePending] = useState(false)
  // Inline rename state — only meaningful when `onRenameInline` is wired.
  // Trello pattern: double-click the title to edit; Enter commits, Escape
  // cancels, blur commits.
  const [renaming, setRenaming] = useState(false)
  const [draftTitle, setDraftTitle] = useState(card.title)
  const titleInputRef = useRef<HTMLTextAreaElement>(null)
  // Click vs. dblclick disambiguation. Browsers fire two `click` events
  // before a `dblclick`, which would open the drawer twice before the
  // rename intent fires. We delay the single-click open by 220ms; if a
  // dblclick arrives in that window, we cancel the open and rename instead.
  const titleClickTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  // BUG-2026-07-02-KANBAN-CARD-LEFTCLICK-LOST — the deferred open must
  // survive a remount (react-query refetch replacing the cards array, lane
  // re-window) inside the 220ms window. The unmount cleanup FLUSHES the
  // pending open instead of discarding it; a dblclick still cancels by
  // nulling the ref before unmount. Refs kept current so the flush fires
  // with the latest card/callback, not a stale closure.
  const pendingOpenRef = useRef<(() => void) | null>(null)
  const onOpenRef = useRef(onOpen)
  onOpenRef.current = onOpen
  const cardRef = useRef(card)
  cardRef.current = card

  useEffect(() => {
    if (!renaming) setDraftTitle(card.title)
  }, [card.title, renaming])
  useEffect(() => {
    if (renaming && titleInputRef.current) {
      titleInputRef.current.focus()
      titleInputRef.current.select()
    }
  }, [renaming])
  useEffect(
    () => () => {
      if (titleClickTimer.current) clearTimeout(titleClickTimer.current)
      // Flush (don't drop) a still-pending single-click open: the operator
      // clicked, the row is unmounting before 220ms — honour the click.
      if (pendingOpenRef.current) {
        const flush = pendingOpenRef.current
        pendingOpenRef.current = null
        flush()
      }
    },
    [],
  )

  const commitRename = async () => {
    const next = draftTitle.trim()
    if (!next || next === card.title) {
      setRenaming(false)
      setDraftTitle(card.title)
      return
    }
    try {
      await onRenameInline?.(card, next)
    } finally {
      setRenaming(false)
    }
  }

  return (
    <div className="flex items-start gap-1.5">
      {/* FEAT-2026-06-20-KANBAN-CARD-TRELLO-CHECK — the completion check
          lives in the title's left gutter (the universally-recognised
          todo/Trello position) instead of the old bottom-right corner the
          operator found unintuitive. Reserved gutter → the title never
          reflows when the hover accent appears. Only rendered when the
          host wires the toggle. */}
      {onToggleComplete && (
        <KanbanCardCompleteButton
          done={card.status === 'DONE'}
          visible={completeVisible}
          pending={togglePending}
          onToggle={() => {
            setTogglePending(true)
            Promise.resolve(onToggleComplete(card)).finally(() => setTogglePending(false))
          }}
        />
      )}
      <div className="flex-1 min-w-0">
        {renaming && onRenameInline ? (
          <textarea
            ref={titleInputRef}
            value={draftTitle}
            onChange={(e) => setDraftTitle(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onBlur={() => void commitRename()}
            onKeyDown={(e) => {
              e.stopPropagation()
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                void commitRename()
              }
              if (e.key === 'Escape') {
                e.preventDefault()
                setDraftTitle(card.title)
                setRenaming(false)
              }
            }}
            rows={2}
            className="w-full resize-none rounded border border-blue-500 bg-white dark:bg-neutral-900 px-1.5 py-1 text-[12.5px] leading-snug text-neutral-900 dark:text-neutral-100 focus:outline-none focus:ring-1 focus:ring-blue-500/30"
          />
        ) : (
          <div
            className={[
              large
                ? 'text-[14px] leading-snug text-neutral-900 dark:text-neutral-100'
                : 'text-[12.5px] leading-snug text-neutral-900 dark:text-neutral-100',
              // Wrap the title instead of truncating it to one ellipsised line
              // (operator 2026-06-23: "wrap the text title on the card so we
              // don't have to open it to read the whole thing"). `break-words`
              // keeps long unbroken refs/subjects from overflowing the card;
              // the line-clamp bounds pathological titles (compact stays a
              // touch tighter than expanded). Compact used to be
              // `line-clamp-1 truncate` → single line + ellipsis.
              'break-words',
              isCompact ? 'line-clamp-3' : 'line-clamp-4',
              // FEAT-2026-06-20-KANBAN-CARD-TRELLO-CHECK — a DONE card
              // strikes through + mutes its title so completion reads at a
              // glance (opacity is theme-safe; the inline --card-fg colour
              // would otherwise override a Tailwind text colour).
              card.status === 'DONE' ? 'line-through opacity-60' : '',
            ].join(' ')}
            // Themed-board contrast (bug 2026-05-15). When the theme
            // sets `--card-fg` the inline `color` wins and the dark/
            // light Tailwind class is overridden. On unthemed boards
            // `var(--card-fg)` is invalid → declaration dropped →
            // Tailwind class wins. Net effect: legible text on every
            // theme without regressing the default white/zinc surfaces.
            style={{ color: 'var(--card-fg)' }}
            onClick={(e) => {
              if (!onRenameInline) return
              e.stopPropagation()
              if (titleClickTimer.current) clearTimeout(titleClickTimer.current)
              pendingOpenRef.current = () => onOpenRef.current?.(cardRef.current)
              titleClickTimer.current = setTimeout(() => {
                const open = pendingOpenRef.current
                pendingOpenRef.current = null
                titleClickTimer.current = null
                open?.()
              }, 220)
            }}
            onDoubleClick={(e) => {
              if (!onRenameInline) return
              e.stopPropagation()
              e.preventDefault()
              if (titleClickTimer.current) {
                clearTimeout(titleClickTimer.current)
                titleClickTimer.current = null
              }
              pendingOpenRef.current = null
              setRenaming(true)
            }}
            title={onRenameInline ? `${card.title}\n(double-click to rename)` : card.title}
          >
            {highlightQuery?.trim()
              ? highlightMatch(card.title, highlightQuery, 'kc-title')
              : card.title}
          </div>
        )}
      </div>
      {card.sensitive && !hideSensitive && (
        <Lock
          className="shrink-0 w-3 h-3 text-purple-600 dark:text-purple-400"
          aria-label="Sensitive"
        />
      )}
      {/* FEAT-2026-05-28-KANBAN-CARD-PIN — small Pin icon when the
          card is pinned. Sits in the title row next to the
          Sensitive lock so the operator's eye picks up the
          pinned state without scanning the whole card. Only
          renders when pinned (no slot reserved for unpinned
          cards — keeps compact density tight). */}
      {card.pinned && !hidePin && (
        // Lucide's typed props don't accept `title`; wrap in a
        // <span> so the operator gets a hover tooltip without
        // tripping LucideProps' SVG-only type guard.
        <span
          className="shrink-0 inline-flex"
          title="Pinned to top of list (press P to unpin)"
          aria-label="Pinned"
        >
          <Pin
            className="w-3 h-3 text-amber-600 dark:text-amber-400 fill-current"
            aria-hidden="true"
          />
        </span>
      )}
      {aiConfidencePct != null && !hideAi && (
        <span
          className={[
            'shrink-0 inline-flex items-center gap-0.5 rounded-full',
            'px-1.5 py-0.5 text-[9.5px] font-medium leading-none',
            lowConfidence
              ? 'bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300 ring-1 ring-amber-200 dark:ring-amber-800/60'
              : 'bg-neutral-100 text-neutral-600 dark:bg-zinc-800/80 dark:text-zinc-400',
          ].join(' ')}
          title={
            lowConfidence
              ? `Low-confidence AI placement (${aiConfidencePct}%) — please verify`
              : `AI confidence ${aiConfidencePct}%`
          }
          aria-label={`AI confidence ${aiConfidencePct} percent`}
        >
          <Sparkles className="w-2.5 h-2.5" aria-hidden="true" />
          {aiConfidencePct}%
        </span>
      )}
    </div>
  )
}
